import Unipile from "@/services/unipile/unipile";
import { useStore } from "vuex";
import type {
  LinkedInBrowseResponse,
  LinkedInImportResponse,
  LinkedInSearchProspect,
} from "@/dtos/Unipile.dto";

export interface LinkedInSearchResult {
  contactsCount: number;
  items: any[];
  total: number;
  url: string;
}

const SALES_NAVIGATOR_PATTERN = /^https?:\/\/(www\.)?linkedin\.com\/sales\/search\/people/;

const resolveAccountId = (override?: string): string | undefined => {
  if (override) return override;
  try {
    const store = useStore();
    return store?.getters?.account?.data?.unipileAccountId;
  } catch {
    return undefined;
  }
};

/**
 * @deprecated Prefer browseLinkedInSearchPage + startLinkedInImportJob.
 *
 * Legacy single-shot search. Kept for any callers still relying on the
 * pre-pagination shape; internally hits the deprecated /linkedin/search
 * endpoint, which the backend now serves via the same paged code path.
 */
export async function searchLinkedInContacts(
  url: string,
  accountId?: string,
  limit?: number
): Promise<LinkedInSearchResult> {
  if (!SALES_NAVIGATOR_PATTERN.test(url)) {
    throw new Error("Invalid LinkedIn Sales Navigator URL");
  }

  try {
    const response = await Unipile.searchLinkedIn({
      url: url.trim(),
      account_id: resolveAccountId(accountId),
      limit,
    });

    if (response.data?.status && response.data?.data) {
      const data = response.data.data;
      return {
        contactsCount: data.total || data.items?.length || 0,
        items: data.items || [],
        total: data.total || 0,
        url: url.trim(),
      };
    }

    throw new Error("Invalid response from LinkedIn search API");
  } catch (error: any) {
    console.error("Error searching LinkedIn contacts:", error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to search contacts. Please try again.";
    throw new Error(errorMessage);
  }
}

export interface BrowsePageRequest {
  url: string;
  sessionId?: string;
  page?: number;
  limit?: number;
  accountId?: string;
}

export async function browseLinkedInSearchPage(
  req: BrowsePageRequest
): Promise<LinkedInBrowseResponse & { items: LinkedInSearchProspect[] }> {
  if (!SALES_NAVIGATOR_PATTERN.test(req.url)) {
    throw new Error("Invalid LinkedIn Sales Navigator URL");
  }

  const response = await Unipile.browseLinkedInSearch({
    url: req.url.trim(),
    account_id: resolveAccountId(req.accountId),
    sessionId: req.sessionId,
    page: req.page,
    limit: req.limit,
  });

  if (!response.data?.status || !response.data?.data) {
    const msg =
      (response.data as any)?.msg || (response.data as any)?.message || "LinkedIn search failed";
    throw new Error(msg);
  }
  return response.data.data;
}

export interface StartImportRequest {
  sessionId: string;
  selectedProspectIds: string[];
  taskType?: string;
  bucketOptionId?: string;
  followUpDate?: string;
  enrichEmail?: boolean;
  enrichPhoneNumber?: boolean;
}

export async function startLinkedInImportJob(
  req: StartImportRequest
): Promise<LinkedInImportResponse> {
  const response = await Unipile.startLinkedInImport(req);

  if (!response.data?.status || !response.data?.data) {
    const msg =
      (response.data as any)?.msg || (response.data as any)?.message || "Failed to start import";
    throw new Error(msg);
  }
  return response.data.data;
}
